import React, { createContext, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import CheckoutPage from "./Pages/ComponentsCheckoutPage/CheckoutPage";
import PaymentSuccessful from "./Pages/ComponentsPaymentSuccessful/PaymentSuccessful";
import PaymentFailed from "./Pages/ComponentsPaymentFailed/PaymentFailed";
import "./overrides";
import LinkUnavailable from "./Pages/ComponentsLinkUnavailable/LinkUnavailable";
import PaymentPage from "./Pages/ComponentsCheckoutPage/PaymentPage";
import ReactGA from "react-ga4";
import { ToastProvider } from "./contexts/ToastContext";

import { trackData, getCookie, setCookie } from "./Utils/attribution/CheckoutAttribution";
import { getReferralCode } from "./Utils/referralCode";
export const AuthContext = createContext({} as any);

export const CartContext = createContext({} as any);

/**
 * Initializes URL params and cookie values into sessionStorage.
 * Called early (index.tsx) so sessionStorage is populated before any component reads it.
 */
export const initParamsFromUrlAndCookies = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const accessToken = urlParams.get("token");
    const itemId = urlParams.get("itemId");
    // Get referral code from URL params first, fallback to cookie (referral_code) from nationalfinanceolympiad.com
    const referredBy = getReferralCode();
    const rollNumber = urlParams.get("rollNumber");
    const pfh = urlParams.get("pfh");
    const couponCode = urlParams.get("coupon");
    const nfo2024 = urlParams.get("nfo2024");
    const expName = urlParams.get("exp_name");

    // Decode base64 pfh parameter if it exists
    if (pfh) {
      const decodedPfh = atob(pfh);
      sessionStorage.setItem("pfh", decodedPfh);
    } else {
      sessionStorage.setItem("pfh", "false");
    }

    // Initialize verification results as empty (not set yet)
    if (!sessionStorage.getItem("pfhVerified")) {
      sessionStorage.removeItem("pfhVerified");
    }
    if (!sessionStorage.getItem("nfo2024Verified")) {
      sessionStorage.removeItem("nfo2024Verified");
    }

    if (accessToken) {
      sessionStorage.setItem("accessToken", accessToken);
    }
    if (itemId) {
      sessionStorage.setItem("itemId", itemId);
    }
    if (referredBy) {
      sessionStorage.setItem("referredBy", referredBy);
    }
    if (rollNumber) {
      sessionStorage.setItem("rollNumber", rollNumber);
      console.log(`Roll number from URL parameter: ${rollNumber}`);
    }
    if (couponCode) {
      sessionStorage.setItem("urlCouponCode", couponCode.toUpperCase());
      console.log(`Coupon code from URL parameter: ${couponCode}`);
    }
    if (nfo2024) {
      sessionStorage.setItem("nfo2024", nfo2024);
      console.log(`NFO2024 parameter from URL: ${nfo2024}`);
    }
    if (expName) {
      sessionStorage.setItem("exp_name", expName);
      console.log(`Experiment name from URL parameter: ${expName}`);
    }

    // If session_id is present in the URL, use it (takes priority over cookie)
    const urlSessionId = urlParams.get("session_id");
    if (urlSessionId) {
      setCookie("session_id", urlSessionId, 30);
      console.log(`session_id from URL parameter stored in cookie: ${urlSessionId}`);
    }

    // Persist UTM params so they can be forwarded in the stadia redirect on the success page
    const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
    utmKeys.forEach((key) => {
      const val = urlParams.get(key);
      if (val) sessionStorage.setItem(key, val);
    });
};

const AppRoutes = () => {
  useEffect(() => {
    trackData("user_viewed_checkoutpage");
  }, []);

  useEffect(() => {
    initParamsFromUrlAndCookies();
    ReactGA.initialize([
      { trackingId: "G-ZGFSQKYL39" },
      { trackingId: "GT-5D48XGC" },
    ]);
    trackData("5678_checkout_pageview");
    ReactGA.event({
      category: "5678_checkout_pageview",
      action: "5678_checkout_pageview",
      label: "5678_checkout_pageview",
    });
  }, []);

  //@ts-ignore
  const CartProvider = ({ children }) => {
    return <CartContext.Provider value={{}}>{children}</CartContext.Provider>;
  };

  return (
    <CartProvider>
      <ToastProvider>
        <BrowserRouter basename="/">
          <Routes>
            <Route
              path="*"
              element={<Navigate to={"/CheckoutPage"} replace />}
            />
            <Route path="/CheckoutPage" element={<CheckoutPage />} />
            <Route path="/PaymentSuccessful" element={<PaymentSuccessful />} />
            <Route path="/PaymentFailed" element={<PaymentFailed />} />
            <Route path="LinkUnavailable" element={<LinkUnavailable />} />
            <Route path="/PaymentPage" element={<PaymentPage />} />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </CartProvider>
  );
};

const App = () => {
  //@ts-ignore
  const AuthProvider = ({ children }) => {
    return <AuthContext.Provider value={{}}>{children}</AuthContext.Provider>;
  };
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

export default App;
